// Third-party
import round from 'lodash/round';

// Namespace
import * as ProposalNS from '../Proposal';

// Utils
import { deepClone } from '../../../utils/objects';
import { formatMessageWithLocale, formatLocalizedString, LOCALE } from '../../../utils/i18n';
import { PROPOSAL_ORIENTATION } from '../utils/constants';
import { OTHER_QUESTION_ITEM } from '@/utils/questionnaire';
import RiskProfileItem from '@/types/risk-profile-item';
import ProposalOrientation from '@/types/proposal-orientation';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Locale from '@/types/locale';
import QuestionItem from '@/types/question-item';
import QuestionnaireSectionItem from '@/types/questionnaire-section-item';
import QuestionAnswer from '@/types/question-answer';
import ProposalOmniRiskProfilesQuestionnaire, {
  ProposalOmniRiskProfilesQuestionnaireQuestion,
  ProposalOmniRiskProfilesQuestionnaireSection,
} from '@/types/risk-profiles/omni/proposal-omni-risk-profiles-questionnaire';

class ProposalInvestorQuestionnaireSectionBuilder {
  newSections: ProposalNS.ProposalTemplateSectionElement[] = [];

  proposalRiskProfile: RiskProfileItem;

  orientation: ProposalOrientation;

  questionnaire: KycQuestionnaire | ProposalOmniRiskProfilesQuestionnaire;

  locale: Locale;

  section: ProposalNS.ProposalTemplateSectionElement;

  constructor(
    section: ProposalNS.ProposalTemplateSectionElement,
    proposalRiskProfile: RiskProfileItem,
    questionnaire: KycQuestionnaire | ProposalOmniRiskProfilesQuestionnaire,
    orientation: ProposalOrientation,
    locale: Locale,
  ) {
    this.section = section;
    this.proposalRiskProfile = proposalRiskProfile;
    this.questionnaire = questionnaire;
    this.orientation = orientation;
    this.locale = locale;
  }

  getQuestionFraction = (question: QuestionItem | ProposalOmniRiskProfilesQuestionnaireQuestion) => {
    const DEFAULT_QUESTION_PAGE_FRACTION = 0.5;
    const printOptions = this.orientation === PROPOSAL_ORIENTATION.LANDSCAPE ? question.printOptions?.landscape : question.printOptions?.portrait;

    return printOptions?.pageFraction || DEFAULT_QUESTION_PAGE_FRACTION;
  };

  splitQuestionnaireInPages() {
    let pageIndex = 0;
    let remainingPageAlloc = 1;

    const newPages: QuestionnaireSectionItem[][] | ProposalOmniRiskProfilesQuestionnaireSection[][] = [];

    for (const section of this.questionnaire.sections) {
      const clonedSection = deepClone(section);

      while (clonedSection.questions.length > 0) {
        // Verify that the next question fits on this page - otherwise bump to next page.
        if (this.getQuestionFraction(clonedSection.questions[0]) > remainingPageAlloc) {
          remainingPageAlloc = 1;
          pageIndex++;
        }

        // Make sure the current page is initialized for that index
        if (!newPages[pageIndex]) {
          newPages[pageIndex] = [];
        }

        let questionsFit = 0;

        for (const question of clonedSection.questions) {
          const pageFraction = this.getQuestionFraction(question);

          if (pageFraction <= remainingPageAlloc) {
            remainingPageAlloc = round(remainingPageAlloc - pageFraction, 1);
            questionsFit++;
          } else {
            break;
          }
        }

        // Make sure the current page is initialized for that index
        if (!newPages[pageIndex]) {
          newPages[pageIndex] = [];
        }

        newPages[pageIndex].push({
          ...clonedSection,
          questions: clonedSection.questions.slice(0, questionsFit),
        });

        clonedSection.questions = clonedSection.questions.slice(questionsFit);
      }
    }

    return newPages;
  }

  build = () => {
    const pages = this.splitQuestionnaireInPages();
    const { title: questionnaireTitle } = this.questionnaire;

    this.newSections = pages.map(
      (sections, index) =>
        ({
          type: ProposalNS.ProposalTemplateElementType.Section,
          key: `INVESTOR_QUESTIONNAIRE INVESTOR_QUESTIONNAIRE_${index}`,
          forced: true,
          pageFraction: {
            Portrait: 1,
            Landscape: 1,
          },
          label: {
            [LOCALE.EN_CA]: `${questionnaireTitle[LOCALE.EN_CA] ? questionnaireTitle[LOCALE.EN_CA] : 'Investor Questionnaire'}`,
            [LOCALE.FR_CA]: `${questionnaireTitle[LOCALE.FR_CA] ? questionnaireTitle[LOCALE.FR_CA] : "Questionnaire de l'investisseur"}`,
          },
          sidebarControl: ProposalNS.ProposalSidebarControl.Hide,
          buildRules: this.section.buildRules,
          disableRules: this.section.disableRules,
          layout: {
            type: 'LAYOUT',
            layout: {
              type: 'GRID',
              columns: 1,
              className: 'investor-questionnaire-grid',
            },
            elements: sections.map(section => ({
              type: 'LAYOUT',
              layout: {
                type: 'GRID',
                columns: 1,
              },
              elements: this.generateQuestionnaireSection(section, section.order),
            })),
          },
          settings: this.section.settings,
          sectionType: 'QUESTIONNAIRE',
        }) as ProposalNS.ProposalTemplateSectionElement,
    );
  };

  generateQuestionnaireSection(section: QuestionnaireSectionItem, sectionNumber: number): ProposalNS.ProposalTemplateElement[] {
    const hasTitle = !!section.title && !!section.title[this.locale];
    return [
      {
        type: 'DATA',
        dataView: {
          type: 'HTML',
          className: 'investor-question',
        },
        DAO: {
          type: 'HTML_CONTENT',
          htmlContentDAOOptions: {
            html: [
              {
                className: 'questionnaire-section-title',
                content: {
                  [LOCALE.EN_CA]: hasTitle ? `Section ${sectionNumber}: ${formatLocalizedString(section.title, this.locale)}` : '',
                  [LOCALE.FR_CA]: hasTitle ? `Section ${sectionNumber} : ${formatLocalizedString(section.title, this.locale)}` : '',
                },
              },
            ],
          },
        },
      } as ProposalNS.ProposalTemplateElement,
      ...section.questions.map(question => {
        const answer = this.proposalRiskProfile.savedSections && this.proposalRiskProfile.savedSections[section._id]?.questions[question._id];
        return this.generateQuestionElement(question, answer);
      }),
    ];
  }

  generateQuestionElement(question: QuestionItem, answer?: QuestionAnswer): ProposalNS.ProposalTemplateElement {
    const printOptions = this.orientation === PROPOSAL_ORIENTATION.LANDSCAPE ? question.printOptions?.landscape : question.printOptions?.portrait;

    const formatImageStyles = (dimensions?: { width?: number; height?: number | string }) => {
      let styles: string = '';

      if (dimensions?.width) {
        styles += `width:${dimensions.width}px;`;
      }

      if (dimensions?.height) {
        styles += `height:${dimensions.height}${dimensions.height === 'auto' ? '' : 'px'};`;
      }

      return styles;
    };

    const renderedOptions = question.options
      .map(option => {
        const isAnswer = answer && answer.options.includes(option._id);
        return `
        <div class="option ${isAnswer ? 'is-answer' : ''}">
          ${isAnswer ? formatMessageWithLocale('Proposal.InvestorQuestionnaire.AnswerPrefix', this.locale) : ''}
          ${formatLocalizedString(option.description, this.locale)}
        </div>
      `;
      })
      .join('');

    const renderedOtherOption = !answer?.options.includes(OTHER_QUESTION_ITEM._id)
      ? ''
      : `
        <div class="option is-answer">
          ${formatMessageWithLocale('Proposal.InvestorQuestionnaire.AnswerOtherPrefix', this.locale)}
          ${answer?.text}
        </div>
      `;

    const questionDescriptionText = !question.description?.text
      ? ''
      : `<h5 class="question-description">${formatLocalizedString(question.description.text, this.locale)}</h5>`;

    const questionDescriptionImg = !question.description?.imgUrl
      ? ''
      : `
        <div class="question-description-image">
          <img
            src="${formatLocalizedString(question.description.imgUrl, this.locale)}"
            style="${formatImageStyles(printOptions?.imgDimensions)}"
          />
        </div>
      `;

    const content = `
      <div class="investor-question-container">
        <span class="question-order">${question.order}</span>
        
        <div class="question-content">
          <h4 class="question-title">
            ${formatLocalizedString(question.title, this.locale)}
          </h4>

          ${questionDescriptionText}

          <div class="image-content-wrapper ${printOptions?.imgPosition || ''}">
            ${questionDescriptionImg}

            <div class="options ${printOptions?.optionsLayout || ''}">
              ${renderedOptions}
              ${renderedOtherOption}
            </div>
          </div>
        </div>
      </div>
    `;

    return {
      type: 'DATA',
      dataView: {
        type: 'HTML',
        className: 'investor-question',
      },
      DAO: {
        type: 'HTML_CONTENT',
        htmlContentDAOOptions: {
          html: [
            {
              content: {
                'en-CA': content,
                'fr-CA': content,
              },
            },
          ],
        },
      },
    } as ProposalNS.ProposalTemplateElement;
  }
}

export const buildInvestorQuestionnaire = (
  section: ProposalNS.ProposalTemplateSectionElement,
  proposalRiskProfile: RiskProfileItem | undefined = undefined,
  questionnaire: KycQuestionnaire | ProposalOmniRiskProfilesQuestionnaire,
  orientation: ProposalOrientation,
  locale: Locale,
): ProposalNS.ProposalTemplateSectionElement[] => {
  // Currently supports only the comparison risk profile - expected.
  if (proposalRiskProfile?.savedSections == null || Object.keys(proposalRiskProfile?.savedSections).length === 0) {
    return [];
  }

  const builder = new ProposalInvestorQuestionnaireSectionBuilder(section, proposalRiskProfile, questionnaire, orientation, locale);
  builder.build();
  return builder.newSections;
};
